import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The game's four sounds: three quick beeps when a turn's programming
 * opens for THIS player, a short beep when the game starts waiting on
 * them mid-turn, a lower one when it starts waiting on the HOST to
 * advance a register, and a tick a second over the last ten seconds of
 * the programming clock. In turn mode, the lower one also marks each
 * register of the turn's replay as it begins.
 *
 * Programming opens after the host has run the last turn out and every
 * end-of-turn answer is in, which a player may not have stayed to watch.
 * The mid-turn prompts -- an option, a weapon, an Abort Switch, a chop
 * shop, the end of the turn -- come while the player is watching other
 * robots move, or is not watching at all. The host's register advance is
 * missed for the same reason: it comes at the end of a playback the host
 * may well have looked away from.
 *
 * The countdown is the exception to all that: the player IS looking at
 * the screen, and what the sound adds is that they need not keep looking
 * at the clock on it while they decide.
 *
 * One mute covers all of them. A player who does not want to be beeped at
 * does not want to be ticked at either.
 *
 * Synthesised rather than loaded from a file, so it needs no asset, no
 * network round trip and no decoding: one oscillator and a gain envelope.
 * The envelope matters — a bare start/stop clicks, because the waveform
 * is cut mid-cycle.
 */

const MUTE_KEY = 'rallyx.beep.muted';

/**
 * The pitches, in Hz. The host hears all three, so they have to be
 * distinguishable without looking: the two prompts are an octave apart,
 * which is as far as they can be pulled and still sound like the same
 * instrument. The lower one is the host's, because it is the one that
 * means "everyone is waiting on you" rather than "answer something".
 *
 * The countdown sits between them, and repeats once a second. The start
 * of programming is told apart by its rhythm rather than its pitch: three
 * beeps close together, at the prompt's pitch since it too means "your
 * move".
 */
const PROMPT_HZ = 880;
/** The host's, and each register of a turn-mode replay as it begins: a
 * turn-mode game never waits on the host, so the pitch is free there. */
const HOST_HZ = 440;
const COUNTDOWN_HZ = 660;

/** The start of programming: this many beeps, this far apart. */
const PROGRAM_BEEPS = 3;
const PROGRAM_GAP_S = 0.2;

/** How many of the timer's last seconds are counted out loud. */
const COUNTDOWN_FROM = 10;

/** Reads the stored preference. Wrapped because storage throws outright
 * in a browser with cookies disabled, and a missing preference is not a
 * reason to fail to render a game. */
function storedMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useTurnBeep(
  waiting: boolean,
  hostWaiting = false,
  /**
   * The programming deadline to count out loud, or null for silence.
   *
   * Null once the player has sent their program: the clock still governs
   * the phase, but they have nothing left to do about it and being chased
   * for ten seconds over a decision already made is only noise. The
   * caller decides that -- this hook knows about sound, not about phases.
   */
  countdownTo: string | null = null,
  /**
   * The turn whose programming this player owes, or null when they owe
   * none. Three beeps each time it becomes a new turn, so taking a
   * program back and sending it again does not sound it twice.
   */
  programmingTurn: number | null = null,
  /**
   * The register a turn-mode replay is playing, as `turn:register`, or
   * null when none is. One beep each time it changes to a new register.
   */
  replayRegister: string | null = null,
): {
  muted: boolean;
  setMuted: (next: boolean) => void;
} {
  const [muted, setMutedState] = useState(storedMuted);
  const context = useRef<AudioContext | null>(null);
  // Whether the previous render was already waiting, so each beep fires on
  // the EDGE rather than on every render while the prompt is up.
  const wasWaiting = useRef(false);
  const wasHostWaiting = useRef(false);

  const setMuted = useCallback((next: boolean) => {
    setMutedState(next);
    try {
      window.localStorage.setItem(MUTE_KEY, next ? '1' : '0');
    } catch {
      // Preference not persisted; the toggle still works for this session.
    }
  }, []);

  /** One tone, `delay` seconds from now. Every sound here is this one at
   * a different pitch or rhythm, so there is one copy of the oscillator
   * and its envelope. */
  const tone = useCallback((hz: number, delay = 0) => {
    try {
      // Created on first use rather than on mount: a context made before
      // any user gesture starts suspended, and browsers keep it that way.
      // By the time a mid-turn prompt appears the player has certainly
      // clicked something, so the context starts running.
      context.current ??= new AudioContext();
      const ctx = context.current;
      if (ctx.state === 'suspended') void ctx.resume();

      const now = ctx.currentTime + delay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;

      // Ramped in and out over a few milliseconds. Without this the abrupt
      // start and stop are audible as clicks either side of the tone.
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gain.gain.setValueAtTime(0.15, now + 0.1);
      gain.gain.linearRampToValueAtTime(0, now + 0.14);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.15);
    } catch {
      // No audio available, or blocked. A game that cannot beep is not a
      // game that should stop working.
    }
  }, []);

  useEffect(() => {
    const rising = waiting && !wasWaiting.current;
    wasWaiting.current = waiting;
    if (rising && !muted) tone(PROMPT_HZ);
  }, [waiting, muted, tone]);

  const programmedFor = useRef<number | null>(null);
  useEffect(() => {
    if (programmingTurn === null || programmingTurn === programmedFor.current) return;
    programmedFor.current = programmingTurn;
    if (muted) return;
    for (let i = 0; i < PROGRAM_BEEPS; i++) tone(PROMPT_HZ, i * PROGRAM_GAP_S);
  }, [programmingTurn, muted, tone]);

  const beatOf = useRef<string | null>(null);
  useEffect(() => {
    if (replayRegister === null || replayRegister === beatOf.current) return;
    beatOf.current = replayRegister;
    if (!muted) tone(HOST_HZ);
  }, [replayRegister, muted, tone]);

  useEffect(() => {
    const rising = hostWaiting && !wasHostWaiting.current;
    wasHostWaiting.current = hostWaiting;
    if (rising && !muted) tone(HOST_HZ);
  }, [hostWaiting, muted, tone]);

  /**
   * The last ten seconds, counted one beep a second.
   *
   * Polled well inside the second rather than on a one-second interval:
   * an interval started partway through a second drifts against the
   * deadline and would tick at the wrong moments, and the browser is free
   * to throttle it in a background tab. Reading the clock each time and
   * beeping on the CHANGE of the whole second means a late or coalesced
   * poll still lands one beep on that second, not several.
   *
   * `spoken` is the second already sounded, so a poll that finds the same
   * one is silent. It is reset by the deadline changing, which is what a
   * new turn looks like.
   */
  const spoken = useRef<number | null>(null);

  useEffect(() => {
    spoken.current = null;
    if (!countdownTo || muted) return undefined;
    const endsAt = new Date(countdownTo).getTime();
    if (Number.isNaN(endsAt)) return undefined;

    const check = () => {
      const left = Math.ceil((endsAt - Date.now()) / 1000);
      // Nothing at zero: the phase ends there, and the sound that matters
      // then is whatever the next screen has to say.
      if (left < 1 || left > COUNTDOWN_FROM) return;
      if (spoken.current === left) return;
      spoken.current = left;
      tone(COUNTDOWN_HZ);
    };

    const timer = setInterval(check, 250);
    check();
    return () => clearInterval(timer);
  }, [countdownTo, muted, tone]);

  useEffect(() => () => { void context.current?.close(); }, []);

  return { muted, setMuted };
}
